// Единый источник правды о сети для UI (`useOnlineStatus`) и TanStack Query
// (`onlineManager`). `navigator.onLine` ненадёжен (captive portal, DevTools) —
// подтверждаем пробой.
//
// Цель пробы зависит от платформы. В Capacitor ассеты отдаются из бандла
// (`https://localhost` / `capacitor://localhost`, см. apps/mobile/capacitor.config.ts),
// поэтому относительный `/version.json` лежит на устройстве и отвечает 200 ВСЕГДА
// — приложение считало себя онлайн даже в самолётном режиме, и весь offline-UX
// (баннер, гейты, очередь сообщений, локальный старт теста) был недостижим.
// На мобиле бьём в абсолютный API-хост.

import { IS_CAPACITOR } from "./platform-detect";

const RECHECK_INTERVAL_MS = 15_000;

/** Лёгкий liveness сервера (без БД/Redis) — дешёвая цель для частой пробы. */
const API_PROBE_PATH = "/health/live";
const WEB_PROBE_PATH = "/version.json";

function probeUrl(): string {
	const cacheBuster = `_probe=${Date.now()}`;
	if (!IS_CAPACITOR) return `${WEB_PROBE_PATH}?${cacheBuster}`;
	const apiUrl = (import.meta.env?.VITE_API_URL ??
		import.meta.env?.VITE_SERVER_URL ??
		"") as string;
	// Без абсолютного хоста проба на мобиле бессмысленна — честнее считать, что
	// сети нет, чем всегда отвечать «онлайн».
	return apiUrl ? `${apiUrl.replace(/\/$/, "")}${API_PROBE_PATH}?${cacheBuster}` : "";
}

/** CustomEvent при переходе offline→online (mutation retries, lazy remount). */
export const STUDYHUB_ONLINE_EVENT = "studyhub:online";

/** Потолок ожидания пробы: залипшая мобильная сеть держит fetch минутами. */
const PROBE_TIMEOUT_MS = 5_000;

export async function probeNetwork(): Promise<boolean> {
	if (typeof fetch === "undefined") return true;
	const url = probeUrl();
	if (!url) return false;
	try {
		// Без таймаута висящие пробы накапливались бы каждые 15 секунд, а флаг
		// online оставался устаревшим всё это время.
		const signal =
			typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
				? AbortSignal.timeout(PROBE_TIMEOUT_MS)
				: undefined;
		const res = await fetch(url, { method: "HEAD", cache: "no-store", signal });
		// Лежащий сервер (5xx) — это не «онлайн» для наших целей: запросы всё
		// равно не пройдут, а UI должен предложить офлайн-путь.
		return res.status < 500;
	} catch {
		return false;
	}
}

/** Текущая оценка online — всегда через probe (не доверяем navigator.onLine). */
export async function resolveOnlineStatus(): Promise<boolean> {
	return probeNetwork();
}

export type OnlineStatusListener = (online: boolean) => void;

/**
 * Последняя известная оценка сети БЕЗ новой пробы. `null` — общий монитор ещё
 * ни разу не отвечал (нет подписчиков / первая проба в полёте).
 *
 * Нужен там, где проба слишком дорога: тесные циклы ожидания (grace-окно перед
 * finalize записи) не должны бить HEAD-ом каждые полторы секунды.
 */
export function getLastKnownOnline(): boolean | null {
	return lastKnown;
}

// ─────────────────────────────────────────────────────────────────────────────
// Единая подписка на всё приложение.
//
// Раньше каждый вызов `subscribeOnlineStatus` заводил СВОЙ интервал и свою
// пробу. Потребителей ~37, включая покарточные компоненты списков: экран из
// полусотни карточек давал полсотни HEAD-запросов каждые 15 секунд, и все
// одновременно при возврате из фона. На мобиле это радиомодуль и батарея.
// Теперь таймер, слушатели и проба общие, а подписчики просто разделяют
// результат.
// ─────────────────────────────────────────────────────────────────────────────

const listeners = new Set<OnlineStatusListener>();
let lastKnown: boolean | null = null;
let intervalId: number | null = null;
let teardown: (() => void) | null = null;
/** Отложенный offline: при resume вкладки первая проба часто ложно падает. */
let offlineEmitTimer: number | null = null;
const OFFLINE_CONFIRM_MS = 1_800;

function clearOfflineTimer(): void {
	if (offlineEmitTimer !== null) {
		window.clearTimeout(offlineEmitTimer);
		offlineEmitTimer = null;
	}
}

function emit(online: boolean): void {
	if (online) clearOfflineTimer();
	if (lastKnown === online) return;
	const wasOffline = lastKnown === false;
	lastKnown = online;
	for (const listener of listeners) listener(online);
	if (online && wasOffline) {
		window.dispatchEvent(new CustomEvent(STUDYHUB_ONLINE_EVENT));
	}
}

function emitMaybeOffline(): void {
	// Offline не мигает на resume: ждём повторного подтверждения.
	if (lastKnown === false) return;
	if (offlineEmitTimer !== null) return;
	offlineEmitTimer = window.setTimeout(() => {
		offlineEmitTimer = null;
		void resolveOnlineStatus().then((stillOnline) => {
			if (!stillOnline) emit(false);
		});
	}, OFFLINE_CONFIRM_MS);
}

async function recheck(): Promise<void> {
	const online = await resolveOnlineStatus();
	if (online) emit(true);
	else emitMaybeOffline();
}

function startShared(): void {
	// Optimistic: сразу показываем online, затем подтверждаем пробой (captive portal).
	const goOnline = (): void => {
		emit(true);
		void recheck();
	};
	const goOffline = (): void => void recheck();
	// Возврат из фона: ОС замораживает таймеры и рвёт сокеты, поэтому
	// периодическая проба «просыпается» с устаревшим значением.
	const onVisible = (): void => {
		if (document.visibilityState === "visible") void recheck();
	};

	window.addEventListener("online", goOnline);
	window.addEventListener("offline", goOffline);
	document.addEventListener("visibilitychange", onVisible);
	intervalId = window.setInterval(() => void recheck(), RECHECK_INTERVAL_MS);
	void recheck();

	teardown = () => {
		if (intervalId !== null) window.clearInterval(intervalId);
		intervalId = null;
		clearOfflineTimer();
		window.removeEventListener("online", goOnline);
		window.removeEventListener("offline", goOffline);
		document.removeEventListener("visibilitychange", onVisible);
	};
}

/**
 * Подписка на изменения сети. Возвращает cleanup.
 * Стартовое значение приходит либо сразу (если проба уже была), либо после неё.
 */
export function subscribeOnlineStatus(listener: OnlineStatusListener): () => void {
	if (typeof window === "undefined") {
		listener(true);
		return () => undefined;
	}

	listeners.add(listener);
	if (lastKnown !== null) listener(lastKnown);
	if (listeners.size === 1) startShared();

	return () => {
		listeners.delete(listener);
		if (listeners.size === 0) {
			teardown?.();
			teardown = null;
		}
	};
}
